import { useRef, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSession,
  ingestProjectDocuments,
  requestProjectReport,
  restoreProjectDocument,
  reunderwriteProject,
  withdrawProjectDocument,
  type AnalysisStatus,
  type DocumentRetrieval,
  type GateDocument,
  type IngestFailure,
  type ProjectDocument,
  type ReportRequestFailure,
  type RestoreFailure,
  type ReunderwriteFailure,
  type WithdrawFailure,
} from '../api.js';
import { actorLabel } from './RetrievalLog.js';
import { documentDownloadUrl, retrieveDocument, documentShareUrl } from '../retrieval.js';
import { hubBaseUrl } from '../config.js';
import { CopyLinkButton } from './CopyLinkButton.js';
import { ReunderwriteRun, type RunTrigger } from './ReunderwriteRun.js';
import { fileToUploadDocument, validateUpload } from '../upload.js';
import { sortedForDisplay } from '../document-types.js';
import { addToQueue, queueBlockers, type QueuedFile } from '../ingest-queue.js';
import { timestamp } from '../format.js';
import { usePrintExpanded } from './usePrintExpanded.js';
import { signInPathFor } from '../return-path.js';

/**
 * What a project record HOLDS, and the governed door for adding to it.
 *
 * `POST /projects/:code/documents` has existed and been unreachable: the only upload affordance in the
 * product is on the home page, and that posts to `/analyses/from-documents` — the customer Deal
 * Analyzer door, which carries a folder-routing hint and writes NO stamp. This is the staff gate. Every
 * file through it is stamped into `document_ingests` with who, when, type and lineage, which is what
 * canon R6 ("no silent saves") requires and what the customer door deliberately does not do.
 *
 * ## The two-question ingest (canon R10), and why the first question changed shape
 *
 * 1. **What kind of document is this?** The filename is read for a guess, the guess is offered, and a
 *    person **confirms or replaces it**. That is R10's "confirm the auto-detected type (honest
 *    'confirm' chip pattern)".
 * 2. **Is this new, or does it supersede something already on the record?** An update names the
 *    document it replaces, so the supersession is visible rather than an overwrite. The api refuses an
 *    update that names no target, so the form makes the target required rather than letting a reader
 *    discover the rule through a 400.
 *
 * ⚠️ **This panel used to argue the opposite, and the argument was wrong.** It asked a person to
 * DECLARE a type from a list, with no default, reasoning that _"putting a model's guess where a human's
 * declaration belongs is exactly what the stamp exists to prevent"_. Defensible in the abstract, and
 * measurably wrong in practice: the field was optional, so `document_ingests.document_type` came back
 * **null on 0 of 16 staging rows**, and the engine's C2 check — which compares a person's declared type
 * against the type a model judged from the content — has been correct-but-silent since it shipped
 * because it never had a declared side to compare (ADR-0052 D4).
 *
 * The confirm chip satisfies both rules at once. **A pre-filled box is not a declaration**: `confirmed`
 * starts false, the submit stays disabled, and nothing reaches the record until a person accepts the
 * guess or picks something else. So R6's "no silent saves" holds, and the field is populated every time.
 *
 * ⚠️ And the guess is drawn from the FILENAME rather than from the model, deliberately. C2 compares the
 * declaration against the model's reading of the CONTENT; seed the declaration from that same model and
 * the check compares it to itself and can never disagree. Two independent sources is what makes it
 * informative.
 *
 * ## The way back OUT is this list
 *
 * Every filename here is the link to `…/documents/:id/download` — the attributable-retrieval route
 * (#664, canon R4). It shipped with nothing pointing at it, which made R4 a route and not a capability:
 * a record you can put files into and cannot get them back out of sends people to the drive, and R4 is
 * what has to hold before the drive can be locked (R11/P6). The mechanics of the click — and why it
 * checks the session before navigating — are in `retrieval.ts`.
 */

/**
 * 🔴 THE VOCABULARY IS CHAD'S, and it used to be ours. See `document-types.ts` for the whole story:
 * this panel offered eleven types we invented, the engine's C2 check judges content against Chad's
 * registered nineteen and compares them EXACTLY, and only `Proforma` and `Other` overlapped. Eight of
 * our eleven could never match a judged value, and "Environmental report" against "Environmental Site
 * Assessment" would have fired a false "confirm the type" on two names for one thing (ADR-0052 D4).
 */

const WITHDRAW_FAILURE: Record<WithdrawFailure, string> = {
  forbidden: 'Your role is read-only on this record, so you can’t withdraw a document from it.',
  not_found: 'That document is no longer on this record. Refresh the page.',
  // The database decides it under a per-document lock, so this is a real outcome rather than a bug:
  // somebody else withdrew it between the page loading and the button being pressed.
  already_withdrawn: 'Someone has already withdrawn that document.',
  server_error: 'Something went wrong withdrawing that document. Try again.',
};

const RESTORE_FAILURE: Record<RestoreFailure, string> = {
  forbidden: 'Your role is read-only on this record, so you can’t restore a document to it.',
  not_found: 'That document is no longer on this record. Refresh the page.',
  not_withdrawn: 'Someone has already restored that document. Refresh the page.',
  server_error: 'Something went wrong restoring that document. Try again.',
};

/**
 * Why a re-underwrite did not start. `in_flight` is here for completeness — when the server names the
 * run already going, the panel takes the reader TO it instead of showing this sentence.
 */
const REUNDERWRITE_FAILURE: Record<ReunderwriteFailure, string> = {
  forbidden: 'Your role is read-only on this record, so you can’t start a re-underwrite.',
  not_found: 'This project record no longer exists. Refresh the page.',
  in_flight: 'An underwrite of this record is already running.',
  nothing_to_judge:
    'Every document on this record has been withdrawn — there is nothing to underwrite.',
  not_living: 'This record is finalized and is no longer re-underwritten.',
  no_project_code: 'This record has no project number yet — promote it before re-underwriting it.',
  unavailable: 'The analysis runner isn’t available right now. Try again shortly.',
  server_error: 'Something went wrong starting the re-underwrite. Try again.',
};

/** Said after a 202 — what happened, and what happens next. Nothing claims the PDF exists yet. */
const REPORT_REQUESTED =
  'Report requested — Nexus files the PDF in the project’s Nexus workspace (Deal Room) when it finishes.';

/** Why a report request was not accepted. */
const REPORT_FAILURE: Record<ReportRequestFailure, string> = {
  forbidden: 'Your role is read-only on this record, so you can’t request a report.',
  tenant_not_allowed: 'Reports can be requested for GGP’s own projects only, for now.',
  not_found: 'This project record no longer exists. Refresh the page.',
  // ⚠️ No advice to re-underwrite: it spends, and cannot clear an archived or pre-binding insight.
  no_published_insight:
    'Nexus shows no published underwrite for this record. It may be archived in the Deal Room, or was published before the record was bound.',
  // A Nexus 404 on the request: the insight is gone, OR Nexus's report service is missing — the
  // client cannot tell which without reading Nexus's prose, so both are named.
  insight_not_found:
    'Nexus answered “not found” — either it no longer holds this record’s insight, or its report service is unavailable. Refresh and try once more; if it persists, raise it with the Nexus team.',
  // The PDF is filed on NEXUS, never on this record — it will not appear in the list above.
  nexus_timeout:
    'Nexus did not answer in time. The report may or may not have been requested — check the project’s Nexus workspace (Deal Room) before asking again. It never appears in this document list.',
  rate_limited: 'Too many report requests — wait a minute, then try again.',
  nexus_tenant_unverified:
    'Nexus could not confirm which tenant our credential belongs to, so no report was requested. Try again shortly.',
  unavailable: 'Nexus isn’t taking report requests right now. Try again shortly.',
  nexus_error: 'Nexus did not accept the report request. Try again; if it persists, tell Brad.',
  server_error: 'Something went wrong requesting the report. Try again.',
};

const FAILURE_MESSAGE: Record<IngestFailure, string> = {
  // Role, not tier: ingesting into a record is an employee act (canon R1), so a viewer is read-only.
  forbidden: 'Your role is read-only on this record, so you can’t add documents to it.',
  not_found: 'This project record no longer exists. Refresh the page.',
  closed: 'This record is finalized and no longer accepts new documents.',
  update_target_not_on_record:
    'The document you chose to update isn’t on this project. Pick one of this project’s documents, or add it as new.',
  too_large: 'One of those files is over the 25 MB limit. Remove it and try again.',
  unavailable: 'The analysis runner isn’t available right now. Try again shortly.',
  server_error: 'Something went wrong adding those documents. Try again.',
};

/**
 * A file at the gate, holding the REAL `File` so its bytes survive to submit.
 *
 * The shape and the rules that govern it live in `ingest-queue.ts` — pure, and tested against real
 * inputs, because the web package has no jsdom and none of R10's actual rules are visible in rendered
 * markup. This component is the wiring; that module is the gate.
 */
type PendingFile = QueuedFile<File>;

/**
 * Who has had one document, on the document's own row.
 *
 * ⚠️ Silent when nobody has taken it. A record where three documents were pulled and eleven were not
 * should show three lines, not fourteen — an audit affordance that annotates every row equally hides
 * the row worth looking at. Absence here means "no handovers", which the panel below states in words
 * so the blank is never read as a rendering fault.
 *
 * `taken` arrives newest-first from the api, so `[0]` is the most recent handover.
 */
function RetrievalSummary({ taken }: { taken: DocumentRetrieval[] }) {
  const latest = taken[0];
  if (!latest) return null;
  return (
    <span className="docs__taken">
      Retrieved {taken.length === 1 ? 'once' : `${taken.length} times`} · last by{' '}
      {actorLabel(latest.retrievedByName)} on {timestamp(latest.retrievedAt)}
    </span>
  );
}

/**
 * The re-underwrite confirm button's words, from the SERVER's judged count. A function so the number it
 * states is testable without driving the two-step confirm, which SSR cannot reach.
 */
/**
 * How many documents the record holds — and an underwrite therefore judges — that this list cannot
 * show: ids in `document_ids` with no intake stamp (they arrived before migration 0032, or by a path
 * that did not stamp). With no stamp there is nothing to withdraw, so every one of them is judged.
 *
 * ⚠️ CLAMPED AT ZERO. Withdrawn ids are stamped and on the record, so they cancel out; a negative
 * difference means nothing, and a negative count rendered as a sentence would be worse than silence.
 *
 * Exported because the page header needs the same number to say honestly how the judged count and
 * the listed count relate — one definition, not two.
 */
export function unlistedJudgedCount(
  recordDocumentCount: number,
  documents: readonly Pick<ProjectDocument, 'onRecord'>[],
): number {
  return Math.max(0, recordDocumentCount - documents.filter((d) => d.onRecord !== false).length);
}

export function reunderwriteConfirmLabel(judged: number | null): string {
  // Unknown (an api that predates the count): say what it does, claim no number.
  if (judged === null) return 'Start a paid re-underwrite';
  return `Start a paid re-underwrite over the ${String(judged)} judged document${judged === 1 ? '' : 's'}`;
}

/**
 * How a document reads in "Which document does it replace?".
 *
 * ⚠️ SAME-NAME DOCUMENTS MUST NOT LOOK IDENTICAL (#1397). The Commons holds three documents named
 * `Copy of …JB_v1.4.xlsm`; a list of three identical options makes "replace the current one" a guess,
 * and the wrong guess files the new bytes as a version of a document nothing pins. The day each was
 * filed is what tells them apart — the same fact the governing-proforma list shows (#1401).
 */
export function updateTargetLabel(
  d: Pick<ProjectDocument, 'filename' | 'versionNumber' | 'submittedAt'>,
): string {
  const version = d.versionNumber > 1 ? ` (v${d.versionNumber})` : '';
  return `${d.filename}${version} — filed ${d.submittedAt.slice(0, 10)}`;
}

export function ProjectDocuments({
  code,
  documents,
  recordDocumentCount,
  judgedDocumentCount,
  retrievals,
  canIngest,
  canRetrieve,
  canReunderwrite,
  canRequestReport,
  highlightDocumentId,
  onIngested,
}: {
  code: string;
  documents: ProjectDocument[];
  /**
   * The record's handovers (canon R4), so each document can say who has had IT. The full chronological
   * ledger is `RetrievalLog`; this is the same rows grouped by document, because a reader looking at a
   * file wants the answer on the file rather than in a list further down the page.
   */
  retrievals: DocumentRetrieval[];
  /**
   * Whether this reader may add documents (owner/admin/member — canon R1). A viewer sees the list and
   * NOT the button: the server enforces this independently, and offering a control that is going to
   * 403 teaches people the product is broken rather than that they lack a permission.
   */
  canIngest: boolean;
  /**
   * Whether this reader may RETRIEVE a document (owner/admin/member — canon R4, "employees retrieve
   * files through the PEE").
   *
   * ⚠️ A viewer still SEES every row — filename, type, version, who added it, who has retrieved it.
   * R4's ledger is meant to be readable; what a viewer does not get is the LINK. Until 2026-08-29 the
   * api had no role gate here at all, so this was not a hidden control but an open door with no
   * handle drawn on it: the filename was a live link to a ~900s presign for the source workbook.
   * The server refuses it now (403) and this stops offering it — both halves, because hiding without
   * the 403 is theatre and the 403 without hiding is a trap that teaches people the product is broken.
   */
  canRetrieve: boolean;
  /**
   * Whether this reader may start a paid re-underwrite of the record (staff, on the Hub — the api
   * requires `model:spend` AND `project:intake`). A viewer is never offered it: the server's 403 is
   * the control, and a button that is going to 403 is a trap.
   */
  canReunderwrite: boolean;
  /**
   * Whether this reader may ask Nexus to render the deal report (staff, on the Hub — the api requires
   * `project:intake`). Required, like `canReunderwrite`, so a page that forgets to pass it fails to
   * compile rather than silently never offering the button.
   */
  canRequestReport: boolean;
  /**
   * The document a SHARED LINK named (`?doc=<id>`), or null on an ordinary visit.
   *
   * ⚠️ It only highlights. It never filters — a shared link that hid the other fourteen documents
   * would answer "here is the file I meant" by removing the record around it, and the record is the
   * point. It also never changes what is retrievable: the link is worth exactly the access its
   * holder already has.
   */
  highlightDocumentId?: string | null;
  /**
   * How many documents the RECORD says it holds (`project_records.document_ids`).
   *
   * 🔴 THIS IS A DIFFERENT NUMBER FROM `documents.length`, AND THE PANEL USED TO PRETEND IT WAS NOT.
   * The list comes from `document_ingests` — the gate's stamp table, which arrived in migration 0032
   * on 2026-08-14. Documents attached to a record before that date, or by any path that did not
   * stamp, are on the record and are not in the list. GGP-2026-0142 is the live case: the header says
   * "2 documents" and the panel said "No documents have been added to this record yet", which is
   * simply false — two were.
   */
  recordDocumentCount: number;
  /**
   * How many documents a re-underwrite will judge — the SERVER's count (`GET /projects/:code`
   * `judgedDocumentCount`, the worker's own rule).
   *
   * 🔴 IT REPLACED A CLIENT ESTIMATE THAT WAS WRONG BY 289. The confirm said "662 current documents"
   * (unwithdrawn stamps plus a guess at unstamped ones) and the run it started judged 373: the stamp
   * table holds documents that have since left the record, and no client arithmetic can see that.
   *
   * NULL when the api did not send it. The verb is still offered then — the server refuses an empty
   * set with a 409 and a sentence — but no number is claimed.
   */
  judgedDocumentCount: number | null;
  /** Refetch the record after an ingest. `Promise<void>`, matching `onApproved`/`onCleared`. */
  onIngested: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  /**
   * Which stamp is being withdrawn, and the note being typed for it.
   *
   * ⚠️ ONE AT A TIME, and the note lives WITH the id rather than in a shared box. A single note field
   * beside a list of documents is how a reason gets attached to the wrong file — and a withdrawal's
   * note is a permanent, unamendable record of why a document left the view.
   */
  const [withdrawing, setWithdrawing] = useState<{ id: string; note: string } | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  /**
   * Which withdrawn document is being RESTORED, and the note for it — the withdrawal's mirror, held to
   * the same one-at-a-time discipline. Since 2026-10-10 this is the only way a withdrawn document comes
   * back: re-uploading the same file leaves it withdrawn (Brad's ruling).
   */
  const [restoring, setRestoring] = useState<{ id: string; note: string } | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  /**
   * The re-underwrite's two steps: offered, then CONFIRMED. It spends — a paid underwrite of the whole
   * set — so one click never starts it, the same discipline the withdrawal's note enforces.
   */
  const [confirmingRerun, setConfirmingRerun] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);
  /**
   * The run being followed after a confirmed re-underwrite (#1396), or — on the Hub — after an upload
   * that started one (#1399). Kept after it ends, so its outcome and result link stay on screen until
   * the next act replaces it.
   *
   * 🔴 `press` IS THE IDENTITY, NOT `analysisId`. A follower that stopped WITHOUT an outcome (session
   * gone, server unreachable, the ceiling) re-offers the verb; pressing it again while the run is still
   * going returns `409 in_flight` with the SAME id. Keyed on the id, React kept the stopped follower —
   * no polling, no `onSettled`, the verb hidden for good. Every act mints a new `press`, so it remounts.
   */
  const [rerunWatch, setRerunWatch] = useState<{
    press: number;
    analysisId: string;
    trigger: RunTrigger;
    submittedAt: string;
  } | null>(null);
  const pressRef = useRef(0);
  const follow = (analysisId: string, trigger: RunTrigger, submittedAt: string) => {
    pressRef.current += 1;
    setRerunWatch({ press: pressRef.current, analysisId, trigger, submittedAt });
    setRerunLive(true);
  };
  /** Whether that run is still going. While it is, the verb is not offered — the notice is the state. */
  const [rerunLive, setRerunLive] = useState(false);
  /** The report request: its own busy flag, so it never disables (or is disabled by) an ingest. */
  const [reportBusy, setReportBusy] = useState(false);
  const [reportNotice, setReportNotice] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  /**
   * The filter box. R9 — "any verb that loses to the old way is a defect": FIND is the verb this
   * panel is used for, and scanning fifteen multi-line rows by eye loses to the drive's own search.
   *
   * ⚠️ FILTERS WHAT IS ALREADY HERE. It is not `search.hybrid` and must not become it: that is
   * CONTENT search across the tri-index (p50 2,465ms), and this is "which of this record's files did
   * I mean". The same reasoning that keeps the Hub's find on our own Postgres.
   */
  const [query, setQuery] = useState('');
  /**
   * Whether the add-documents gate is unfolded.
   *
   * ⚠️ STATE, NOT A DERIVED `open`. A `<details open={expr}>` whose expression is recomputed each
   * render snaps back on the next re-render — a reader who opened it by hand would have it shut under
   * them the moment anything else changed. `onToggle` keeps the DOM and the state agreed.
   *
   * Open on a record with nothing on it, because there is nothing to read and adding is the only act
   * available. Initial-only: documents arriving on a later reload must not fold the gate away while
   * somebody is standing at it.
   */
  const [addOpen, setAddOpen] = useState(documents.length === 0);
  /*
    ⚠️ THE WITHDRAWN GROUP MUST EXPAND IN PRINT. A printed record is the artifact somebody forwards,
    and a fold that stays shut on paper drops documents the record demonstrably still holds — the
    exact defect Chad's Hazel Street UAT pass caught on the Findings section.
  */
  const withdrawnRef = usePrintExpanded();
  // The older current documents fold the same way, and must print the same way (Brad, 2026-10-01).
  const olderRef = usePrintExpanded();
  // And so do the documents on file that are not judged — still record history, still on paper.
  const notJudgedRef = usePrintExpanded();
  /*
    The gate has no record content to lose on paper, but the guard is categorical and rightly so: the
    rule "a <details> we render is expanded in print" is cheap to keep and expensive to re-litigate
    per component. A printed panel showing the gate open is honest — it is what the panel offers.
  */
  const addRef = usePrintExpanded();
  // The hidden input the drop zone clicks. Kept as a real input so the keyboard and screen-reader
  // path is the same control, not a parallel one.
  const fileInput = useRef<HTMLInputElement>(null);

  // Grouped once per render rather than filtered per row — the ledger is capped at 200 and the
  // document list is small, but an O(docs × retrievals) scan inside the map is the kind of thing that
  // is fine until the busiest record arrives.
  // Documents the record holds (and judges) that the gate cannot list — see `unlistedJudgedCount`.
  const unstamped = unlistedJudgedCount(recordDocumentCount, documents);

  const byDocument = new Map<string, DocumentRetrieval[]>();
  for (const r of retrievals) {
    const list = byDocument.get(r.documentId);
    if (list) list.push(r);
    else byDocument.set(r.documentId, [r]);
  }

  /*
    ⚠️ THE WITHDRAWAL NOTE IS SEARCHABLE, deliberately rather than incidentally. "Why is the old
    drawing set not counting?" is answered by the note, and a filter that could not reach it would
    send the reader back to reading every row — which is the thing the box exists to replace.
  */
  const matches = (d: ProjectDocument, needle: string): boolean =>
    [d.filename, d.documentType, d.submittedByName, d.withdrawalNote]
      .filter((v): v is string => typeof v === 'string')
      .some((v) => v.toLowerCase().includes(needle));

  const needle = query.trim().toLowerCase();
  const filtering = needle !== '';
  const shown = filtering ? documents.filter((d) => matches(d, needle)) : documents;

  /*
    🔴 THE SPLIT THIS PANEL WAS MISSING. Canon keeps a withdrawn document visible — "it stays in the
    store and in lineage, retrievable and attributed like any other" — so it must not be hidden. But
    it has left the CURRENT-DOCUMENTS lens, and rendering it inline among the live files makes a
    reader scan the record's history to find the record's contents.

    So: live stays open, withdrawn collapses. The same rule the Record tab settled on — what a reader
    is working from stays open, what they may need to consult folds away with its count on the label.
  */
  const withdrawn = shown.filter((d) => d.withdrawnAt !== null);
  /*
    🔴 AND THE SPLIT IT WAS MISSING AFTER THAT. A stamp is not a place on the record: some of these
    rows are documents whose id has LEFT `document_ids` — pruned when Nexus no longer had them,
    replaced by a duplicate, removed by hand, or uploaded before #1311 attached Hub uploads. An
    underwrite never reads them, and listing them among the judged files made 727 look like 727 inputs
    to a run that read 373. The server says which is which (`judged`); this only groups.

    ⚠️ AN EXHAUSTIVE PARTITION of the un-withdrawn rows — `judged` or not — so no row can fall
    between the two groups and vanish, whatever the server's flags say.
  */
  const live = shown.filter((d) => d.withdrawnAt === null && d.judged !== false);
  const notJudged = shown.filter((d) => d.withdrawnAt === null && d.judged === false);
  const noMatches = filtering && shown.length === 0;

  /*
    ⚠️ 662 ROWS IS NOT A PANEL, IT IS A SCROLL (Brad, 2026-10-01 — The Commons). The verbs a reader came
    for (re-underwrite, add documents) sat below six hundred rows. So the newest arrivals stay open —
    the API orders newest first — and the rest fold behind their count. Nothing is hidden: the count is
    on the label, the filter searches all of them, and a filtered list is never folded (a search that
    matched a folded row would report a hit the reader cannot see).
  */
  const RECENT = 10;
  const foldOlder = !filtering && live.length > RECENT;
  const recent = foldOlder ? live.slice(0, RECENT) : live;
  const older = foldOlder ? live.slice(RECENT) : [];

  /**
   * Add files to the queue — from the picker or from a drop.
   *
   * ⚠️ APPENDS, and it did not used to. The old version REPLACED the queue on every selection, which
   * is invisible with one file and destroys work the moment R10's "bulk drops supported" is real: drag
   * in the drawings, then drag in the budget, and the drawings you had already typed vanish. De-duped
   * on name+size so re-dropping the same file does not double it (the same key `DocUpload` uses).
   *
   * Each file arrives with `suggested` — a guess from its FILENAME, never a declaration. The person
   * confirms or changes it, and that confirmation is the declaration (canon R10's confirm chip, R6's
   * no-silent-saves). A file whose name says nothing arrives with an empty box and no guess to accept.
   */
  function addFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError(null);
    setPending((prev) => addToQueue(prev, Array.from(files)));
  }

  /** Drop one queued file. Once you can drag files in by the handful you need a way to take one out. */
  function removeFile(index: number) {
    setPending((prev) => prev.filter((_, i) => i !== index));
    setError(null);
  }

  /**
   * Retrieve one document. The tab is opened in this synchronous prologue — before any `await` — or the
   * browser blocks it as a popup; `retrieveDocument` then points it at the route once the session is
   * known good. `getSession` goes through the api client's refresh-and-replay, so a lapsed access cookie
   * is renewed here rather than surfacing as a raw 401 page after the navigation.
   */
  async function retrieve(documentId: string) {
    const outcome = await retrieveDocument(
      {
        openTab: () => window.open('', '_blank'),
        ensureSession: async () => (await getSession()) !== null,
        goTo: (url) => window.location.assign(url),
      },
      code,
      documentId,
    );
    if (outcome === 'signed-out')
      void navigate(signInPathFor(window.location, window.location.origin), { replace: true });
  }

  /**
   * Record a withdrawal. Appends an event; nothing is deleted.
   *
   * The note is trimmed and required here as well as in the api and the database. Three checks is not
   * belt-and-braces theatre: this one gives the person a disabled button and a reason, the api gives a
   * usable refusal to any other client, and the CHECK makes it true for a repair script.
   */
  async function submitWithdrawal() {
    const target = withdrawing;
    if (!target || target.note.trim() === '' || busy) return;
    setBusy(true);
    setWithdrawError(null);
    const res = await withdrawProjectDocument(code, target.id, target.note.trim());
    setBusy(false);
    if (!res.ok) {
      setWithdrawError(WITHDRAW_FAILURE[res.reason]);
      return;
    }
    setWithdrawing(null);
    await onIngested();
  }

  /** Record a restore. Appends an event; the withdrawal stays on the record beside it. */
  async function submitRestore() {
    const target = restoring;
    if (!target || target.note.trim() === '' || busy) return;
    setBusy(true);
    setRestoreError(null);
    const res = await restoreProjectDocument(code, target.id, target.note.trim());
    setBusy(false);
    if (!res.ok) {
      setRestoreError(RESTORE_FAILURE[res.reason]);
      return;
    }
    setRestoring(null);
    await onIngested();
  }

  /**
   * Start the re-underwrite, and follow it HERE. On success — or when one is ALREADY running and the
   * server names it — the panel follows that run in place (`ReunderwriteRun`) and links to its result
   * when it ends. Every other refusal stays here with a sentence.
   *
   * 🔴 IT USED TO `navigate('/analyses/:id')`, AND ON THE HUB THAT IS A REDIRECT TO `/projects` (#1396).
   * The verb is offered only on the Hub, and the Hub has no `/analyses` route — so every successful
   * press dropped the reader on the project list with no sign the paid run had started. Do not put a
   * navigation back here; the result link in `ReunderwriteRun` is the way to the report.
   */
  async function submitReunderwrite() {
    if (busy) return;
    setBusy(true);
    setRerunError(null);
    const submittedAt = new Date().toISOString();
    const res = await reunderwriteProject(code);
    setBusy(false);
    const runId = res.ok ? res.analysisId : res.reason === 'in_flight' ? res.analysisId : undefined;
    if (runId) {
      setConfirmingRerun(false);
      follow(runId, res.ok ? 'reunderwrite' : 'joined', submittedAt);
      return;
    }
    if (!res.ok) setRerunError(REUNDERWRITE_FAILURE[res.reason]);
  }

  /**
   * The followed run ended (or this page stopped following it). Re-offer the verb; on a SUCCESS, also
   * refetch the record — the header, facts and basis are the new underwrite's now, and a page still
   * showing the old numbers under "Re-underwrite finished" would contradict itself.
   *
   * ⚠️ A refetch that REJECTS must not cost the reader the outcome: the finished notice lives in the
   * follower's own state, so the rejection is swallowed here rather than left unhandled.
   */
  function rerunSettled(status: AnalysisStatus | null) {
    setRerunLive(false);
    if (status === 'succeeded') void onIngested().catch(() => undefined);
  }

  /**
   * Ask Nexus to render the deal report. Success is said as what it is — REQUESTED — because the PDF
   * arrives later, from Nexus, and this panel cannot see it land.
   */
  async function submitReportRequest() {
    if (reportBusy) return;
    setReportBusy(true);
    setReportError(null);
    setReportNotice(null);
    const res = await requestProjectReport(code);
    setReportBusy(false);
    if (res.ok) setReportNotice(REPORT_REQUESTED);
    else setReportError(REPORT_FAILURE[res.reason]);
  }

  function update(i: number, patch: Partial<PendingFile>) {
    setPending((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  }

  // Every update must name what it supersedes — the gate's second question, enforced here so the
  // reader is stopped by a disabled button and a visible requirement rather than by a 400.
  /**
   * Both gate questions must be answered before anything is sent, and the reader is stopped by a
   * disabled button with a visible reason rather than by a 400.
   *
   * ⚠️ `unconfirmed` is the half that is new, and it is what makes the type field actually get filled.
   * A guess in the box is not an answer; `confirmed` only goes true when a person says so.
   */
  const { unconfirmed, missingTarget } = queueBlockers(pending);
  const incomplete = unconfirmed > 0 || missingTarget;

  async function submit() {
    if (pending.length === 0 || incomplete || busy) return;
    setBusy(true);
    setError(null);
    try {
      // Size/count limits checked BEFORE reading bytes — a friendly message beats a 413 after the
      // browser has spent time base64-ing 14 MB. Same guard the home-page upload uses.
      const invalid = validateUpload(
        pending.map((p) => ({ name: p.file.name, size: p.file.size })),
      );
      if (invalid) {
        setError(invalid);
        setBusy(false);
        return;
      }
      const payload: GateDocument[] = await Promise.all(
        pending.map(async (p) => {
          const doc = await fileToUploadDocument(p.file);
          return {
            ...doc,
            documentType: p.documentType || null,
            lineage: p.lineage,
            targetDocumentId: p.lineage === 'update' ? p.targetDocumentId : null,
          };
        }),
      );
      const res = await ingestProjectDocuments(code, payload);
      if (!res.ok) {
        setError(FAILURE_MESSAGE[res.reason]);
        setBusy(false);
        return;
      }
      setPending([]);
      setBusy(false);
      const submittedAt = new Date().toISOString();
      await onIngested();
      /*
        Follow the re-underwrite. Adding a document UPDATES the deal — the run re-reads the record's
        whole document set — so following that run is the honest completion of the action.

        🔴 FOLLOWED IN PLACE, ON BOTH SURFACES (#1399). This used to `navigate('/analyses/:id')`, which on
        the Hub — no such route — bounced the reader to `/projects` straight after an upload, exactly as
        the re-underwrite did (#1396). The follower links to the run when it ends (cross-origin on the
        Hub, in-app on the analyzer). One behavior on both, rather than a navigate kept for the analyzer
        only: the reader who just added documents to a RECORD is better left on the record.
      */
      if (res.analysisId) follow(res.analysisId, 'upload', submittedAt);
    } catch {
      setError('We couldn’t read one of those files. Try again.');
      setBusy(false);
    }
  }

  /**
   * One document row. Lifted out of the list so the live set and the collapsed withdrawn group
   * render the SAME row — a withdrawn document keeps every affordance a live one has (it is still
   * retrievable and still attributed, which is canon); it simply sits behind a fold.
   */
  const renderRow = (d: ProjectDocument) => (
    <li
      key={d.documentId}
      className={highlightDocumentId === d.documentId ? 'docs__row docs__row--shared' : 'docs__row'}
    >
      {/* A real `href`, not a button styled as a link: the row reads as a document, and
                middle-click / "open in new tab" / "copy link address" all do the obvious thing. The
                handler takes over the ordinary click only to renew the session first. */}
      {canRetrieve ? (
        <a
          className="docs__name"
          href={documentDownloadUrl(code, d.documentId)}
          onClick={(e) => {
            e.preventDefault();
            void retrieve(d.documentId);
          }}
        >
          {d.filename}
        </a>
      ) : (
        /* Same row, same name, no href — a viewer reads the record without being handed the file.
           Deliberately not a disabled link: an <a> with no href is still announced as a link by a
           screen reader, and the honest rendering of "you may read that this exists" is text. */
        <span className="docs__name docs__name--plain">{d.filename}</span>
      )}
      <span className="docs__meta">
        {d.documentType ?? 'Unclassified'}
        {/* Version is shown only once there IS a version history — "v1" on every row is noise
                  that makes the one document at v3 harder to notice, not easier. */}
        {d.versionNumber > 1 && ` · v${d.versionNumber}`} ·{' '}
        {/* WHO put it here — canon R2's first question, which this row could not answer. The
                  payload carried `submittedBy` (a uuid) and this line said "added <date>": when,
                  never who, on the panel whose whole subject is what entered the record.

                  ⚠️ NOT `actorLabel` — its "Name not recorded" asserts that a PERSON acted and we
                  lost the name, which is true on the retrieval ledger and false here. A null
                  `submittedByName` most often means a connector-synced arrival with no human
                  submitter at all, so the row says nothing about who rather than inventing one.
                  (A human stamp written before migration 0032 reads the same way; there is no
                  honest way to tell them apart after the fact, and no honest backfill.) */}
        {d.submittedByName
          ? `added by ${d.submittedByName} on ${timestamp(d.submittedAt)}`
          : `added ${timestamp(d.submittedAt)}`}
      </span>
      {/* Who has had THIS file — one of the two questions the ledger's indexes were built for.
                Rendered only where there is something to report: "Retrieved 0 times" on every row is
                the noise that makes the one document three people pulled harder to spot. */}
      <RetrievalSummary taken={byDocument.get(d.documentId) ?? []} />

      {/*
              🔴 A WITHDRAWN DOCUMENT IS STILL SHOWN, and that is the design rather than a leak.
              Canon: "the document stays in the store and in lineage, retrievable and attributed
              like any other" — it has left the CURRENT-DOCUMENTS lens, not the record. Hiding it
              would make the record quietly disagree with itself, and a reader six months from now
              is meant to see that the drawing set was here, that it did not belong, and who said so.
            */}
      {d.withdrawnAt !== null && (
        <span className="docs__withdrawn">
          Withdrawn{d.withdrawnByName ? ` by ${d.withdrawnByName}` : ''} on{' '}
          {timestamp(d.withdrawnAt)}
          {d.withdrawalNote ? ` — ${d.withdrawalNote}` : ''}
        </span>
      )}
      {/* 🔴 SAID OUT LOUD: a file arrived AFTER the withdrawal (a re-upload, or a new version) and the
          document is still withdrawn. Without this line the row reads "added … today" beside
          "Withdrawn", and a reader assumes the upload brought it back — which it used to, silently,
          until Brad's ruling of 2026-10-10. Only a restore does. Off the record there is no Restore
          to point at (see the button below), so that clause is not offered there. */}
      {d.withdrawnAt !== null && Date.parse(d.submittedAt) > Date.parse(d.withdrawnAt) && (
        <span className="docs__withdrawn">
          {d.onRecord === false
            ? 'Uploaded again since it was withdrawn — it stays withdrawn.'
            : 'Uploaded again since it was withdrawn — it stays withdrawn until someone restores it.'}
        </span>
      )}

      {/* canon R9's SHARE verb. Offered on every row, live or withdrawn, and to every reader
          including a viewer: the link points at the RECORD, not the store (R3), so it is worth
          exactly the access its holder already has and grants nothing by being pasted. */}
      <CopyLinkButton
        url={documentShareUrl(hubBaseUrl, code, d.documentId)}
        label={`Copy link to ${d.filename}`}
      />

      {/* The verb, offered only to someone who may use it and only on a live document. Not on a
          document that is on file but not judged: the underwrite never reads it, so withdrawing it
          would change nothing — a verb with no effect reads as a broken one. It stays retrievable. */}
      {canIngest &&
        d.withdrawnAt === null &&
        d.judged !== false &&
        withdrawing?.id !== d.ingestId && (
          <button
            type="button"
            className="docs__withdrawbtn"
            disabled={busy}
            onClick={() => {
              setWithdrawError(null);
              setWithdrawing({ id: d.ingestId, note: '' });
            }}
          >
            Withdraw
          </button>
        )}

      {withdrawing?.id === d.ingestId && (
        <div className="docs__withdraw">
          <label className="docs__q">
            <span>Why is this being withdrawn?</span>
            <textarea
              rows={2}
              value={withdrawing.note}
              disabled={busy}
              placeholder="e.g. Filed on the wrong record — belongs to 2025-021."
              onChange={(e) => setWithdrawing({ id: d.ingestId, note: e.target.value })}
            />
          </label>
          {/* Said before the act, not after. The note is permanent and unamendable, and the
                    document does not leave the record — both are surprises if discovered later. */}
          <p className="card__note">
            This takes the document out of the record’s current view. It stays in the store, with
            its history and your note — nothing is deleted.
          </p>
          {withdrawError !== null && <p className="notice error">{withdrawError}</p>}
          <button
            type="button"
            className="docs__submit"
            disabled={busy || withdrawing.note.trim() === ''}
            onClick={() => void submitWithdrawal()}
          >
            {busy ? 'Withdrawing…' : 'Withdraw this document'}
          </button>
          <button
            type="button"
            className="docs__withdrawbtn"
            disabled={busy}
            onClick={() => {
              setWithdrawing(null);
              setWithdrawError(null);
            }}
          >
            Cancel
          </button>
          {withdrawing.note.trim() === '' && (
            <p className="card__note">A withdrawal needs a note saying why.</p>
          )}
        </div>
      )}

      {/* RESTORE — the withdrawal's mirror, offered only on a withdrawn document and only to someone
          who may withdraw one. 🔴 THE ONLY WAY BACK (Brad, 2026-10-10): uploading the same file again
          leaves it withdrawn, and this row keeps saying so until a person restores it here.
          ⚠️ NOT on a document that is off the record (`onRecord === false`): restoring one lifts the
          withdrawal, but nothing reads it — it is not in `document_ids` — so the confirm text below
          ("the next underwrite reads it") would be false. */}
      {canIngest &&
        d.withdrawnAt !== null &&
        d.onRecord !== false &&
        restoring?.id !== d.ingestId && (
          <button
            type="button"
            className="docs__withdrawbtn"
            disabled={busy}
            onClick={() => {
              setRestoreError(null);
              setRestoring({ id: d.ingestId, note: '' });
            }}
          >
            Restore
          </button>
        )}

      {restoring?.id === d.ingestId && (
        <div className="docs__withdraw">
          <label className="docs__q">
            <span>Why is this coming back?</span>
            <textarea
              rows={2}
              value={restoring.note}
              disabled={busy}
              placeholder="e.g. It was the right record after all."
              onChange={(e) => setRestoring({ id: d.ingestId, note: e.target.value })}
            />
          </label>
          <p className="card__note">
            This puts the document back into the record’s current view, and the next underwrite
            reads it. The withdrawal and your note both stay on the record.
          </p>
          {restoreError !== null && <p className="notice error">{restoreError}</p>}
          <button
            type="button"
            className="docs__submit"
            disabled={busy || restoring.note.trim() === ''}
            onClick={() => void submitRestore()}
          >
            {busy ? 'Restoring…' : 'Restore this document'}
          </button>
          <button
            type="button"
            className="docs__withdrawbtn"
            disabled={busy}
            onClick={() => {
              setRestoring(null);
              setRestoreError(null);
            }}
          >
            Cancel
          </button>
          {restoring.note.trim() === '' && (
            <p className="card__note">A restore needs a note saying why.</p>
          )}
        </div>
      )}
    </li>
  );

  return (
    <section className="card">
      <h3 className="card__title">Documents</h3>

      {documents.length === 0 ? (
        // ⚠️ TWO DIFFERENT EMPTY STATES, and collapsing them told a lie. A record with nothing on it
        // and a record whose documents predate the ingest stamp are not the same record, and the
        // second one has files a person may be looking for.
        unstamped > 0 ? (
          <p className="card__note">
            This record references{' '}
            <strong>
              {unstamped} document{unstamped === 1 ? '' : 's'}
            </strong>{' '}
            with no ingest stamp — nothing is recorded about who added{' '}
            {unstamped === 1 ? 'it' : 'them'}, when, or what{' '}
            {unstamped === 1 ? 'it supersedes' : 'they supersede'}, so{' '}
            {unstamped === 1 ? 'it cannot' : 'they cannot'} be listed or retrieved here. Documents
            added through this panel are stamped.
          </p>
        ) : (
          <p className="card__note">No documents have been added to this record yet.</p>
        )
      ) : (
        <>
          {/*
            THE FILTER. Rendered whenever the record holds anything, with no size threshold: a box
            that appears at the sixth document is a box a reader has to discover twice.
          */}
          <label className="docs__find">
            <span className="df__label">Find a document</span>
            <input
              type="search"
              value={query}
              placeholder="filename, type, who added it, a withdrawal note…"
              onChange={(e) => setQuery(e.target.value)}
            />
            <span className="df__hint">
              {filtering
                ? `${String(shown.length)} of ${String(documents.length)} documents`
                : `${String(documents.length)} document${documents.length === 1 ? '' : 's'} on file`}
              {/* ⚠️ Says what it searched. A reader who finds nothing has to know whether the file is
                  absent or simply not named the way they guessed — and this box reads names and
                  labels held HERE, never the contents of the files. */}
              {' — matched on name, type, who added it, and withdrawal notes, not file contents.'}
            </span>
          </label>

          {noMatches ? (
            <p className="card__note">
              Nothing on this record matches <strong>{query.trim()}</strong>. The filter reads
              filenames and labels, not what is inside the files.
            </p>
          ) : (
            <>
              {recent.length > 0 && <ul className="docs">{recent.map(renderRow)}</ul>}

              {/* Open when it holds the document a share link named — same reasoning as the
                  withdrawn group below: a highlighted row inside a shut fold is a lost link. */}
              {older.length > 0 && (
                <details
                  className="docs__withdrawngroup docs__oldergroup"
                  ref={olderRef}
                  open={older.some((d) => d.documentId === highlightDocumentId)}
                >
                  <summary>
                    Show all {live.length} judged documents ({older.length} older)
                  </summary>
                  <ul className="docs">{older.map(renderRow)}</ul>
                </details>
              )}

              {/*
                The judged documents the list CANNOT show. Without this line the header's judged count
                and the rows above disagree with no explanation — the record holds them, the underwrite
                reads them, and they carry no intake stamp to list.
              */}
              {unstamped > 0 && (
                <p className="card__note docs__unlisted">
                  {unstamped === 1
                    ? '1 more judged document has no intake record, so it can’t be listed here.'
                    : `${String(unstamped)} more judged documents have no intake record, so they can’t be listed here.`}
                </p>
              )}

              {/*
                ON FILE, NOT JUDGED. Collapsed like the withdrawn group, with its count on the label and
                one sentence saying why it is not read — nothing is hidden, and nothing is presented as
                evidence that is not. Open while filtering and when a share link names one of its rows,
                for the same reasons as the withdrawn group below.
              */}
              {notJudged.length > 0 && (
                <details
                  className="docs__withdrawngroup docs__notjudgedgroup"
                  ref={notJudgedRef}
                  open={filtering || notJudged.some((d) => d.documentId === highlightDocumentId)}
                >
                  <summary>
                    {notJudged.length} document{notJudged.length === 1 ? '' : 's'} on file but not
                    judged
                  </summary>
                  <p className="card__note">
                    {notJudged.length === 1 ? 'This was' : 'These were'} added to this record but{' '}
                    {notJudged.length === 1 ? 'is' : 'are'} not part of what an underwrite reads —
                    removed or replaced in Nexus, added before an upload fix, or never finished
                    joining the record. Kept here so {notJudged.length === 1 ? 'it' : 'they'} can
                    still be found; opening one works wherever the file still exists.
                  </p>
                  <ul className="docs">{notJudged.map(renderRow)}</ul>
                </details>
              )}

              {/*
                🔴 COLLAPSED, NOT HIDDEN. Canon keeps a withdrawn document on the record —
                "retrievable and attributed like any other" — so it stays reachable, with its count on
                the label. What changed is that it no longer sits between the reader and the files the
                record is actually working from.

                ⚠️ `open` while filtering: a search that matched a withdrawn document and left the
                group shut would report a hit the reader cannot see, which is worse than no hit.

                ⚠️ AND OPEN WHEN IT HOLDS THE DOCUMENT A SHARE LINK NAMED. A share may point at a
                withdrawn document — the row keeps every affordance a live one has, which is canon —
                and without this the recipient lands on a gold-highlighted row inside a CLOSED fold.
                They see an ordinary list, hunt, and lose to the drive: the exact R9 failure the
                share verb exists to remove. Same reasoning as `filtering`, one case over.
              */}
              {withdrawn.length > 0 && (
                <details
                  className="docs__withdrawngroup"
                  ref={withdrawnRef}
                  open={filtering || withdrawn.some((w) => w.documentId === highlightDocumentId)}
                >
                  <summary>
                    {withdrawn.length} withdrawn document{withdrawn.length === 1 ? '' : 's'} — off
                    the current set, still on the record
                  </summary>
                  <ul className="docs">{withdrawn.map(renderRow)}</ul>
                </details>
              )}

              {/* Every live document filtered away while withdrawn ones matched. Saying so stops the
                  reader concluding the record holds nothing by that name. */}
              {live.length === 0 && withdrawn.length + notJudged.length > 0 && (
                <p className="card__note">
                  {filtering
                    ? 'Nothing that is judged matches — the matches are in the groups below.'
                    : notJudged.length === 0
                      ? 'Every document on this record has been withdrawn.'
                      : 'None of the documents listed here is judged.'}
                </p>
              )}
            </>
          )}
        </>
      )}

      {/*
        The run being followed — OUTSIDE the re-underwrite block, because an upload starts one too, and
        a member who may add documents is not necessarily offered the paid verb. Keyed by PRESS, not by
        run id: see `rerunWatch`.
      */}
      {rerunWatch !== null && (
        <div className="docs__reunderwrite">
          <ReunderwriteRun
            key={rerunWatch.press}
            analysisId={rerunWatch.analysisId}
            trigger={rerunWatch.trigger}
            submittedAt={rerunWatch.submittedAt}
            onSettled={rerunSettled}
          />
        </div>
      )}

      {/*
        🔴 THE VERB A WITHDRAWAL WAS MISSING. Withdrawing a document queues nothing — the underwrite
        honours it on its NEXT run, and until this button the only ways to start one without adding a
        document were a file landing in a watched folder or Nexus's Deal Room. Offered only to staff,
        and only when there is something left to judge.
      */}
      {canReunderwrite && (judgedDocumentCount === null || judgedDocumentCount > 0) && (
        <div className="docs__reunderwrite">
          <p className="card__note">Withdrawals take effect on the next underwrite.</p>
          {rerunLive ? null : !confirmingRerun ? (
            <button
              type="button"
              className="docs__submit"
              disabled={busy}
              onClick={() => {
                setRerunError(null);
                setConfirmingRerun(true);
              }}
            >
              Re-underwrite now
            </button>
          ) : (
            <div className="docs__withdraw">
              {/* Said before the act: it costs a model run, and it re-judges the WHOLE current set. */}
              <p className="card__note">
                This re-reads every current document and underwrites the deal again. It is a paid
                run; withdrawn documents are left out.
              </p>
              {rerunError !== null && <p className="notice error">{rerunError}</p>}
              <button
                type="button"
                className="docs__submit"
                disabled={busy}
                onClick={() => void submitReunderwrite()}
              >
                {busy ? 'Starting…' : reunderwriteConfirmLabel(judgedDocumentCount)}
              </button>
              <button
                type="button"
                className="docs__withdrawbtn"
                disabled={busy}
                onClick={() => {
                  setConfirmingRerun(false);
                  setRerunError(null);
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/*
        The deal-report PDF, on demand. Nexus renders it from the record's latest published underwrite
        and files it in the project's workspace; nothing here waits for that. No model spend, so one
        click (the server rate-limits). Staff only; the server also refuses every tenant but GGP's own.
      */}
      {canRequestReport && (
        <div className="docs__reunderwrite">
          {reportNotice !== null && <p className="notice">{reportNotice}</p>}
          {reportError !== null && <p className="notice error">{reportError}</p>}
          <button
            type="button"
            className="docs__submit"
            disabled={reportBusy}
            onClick={() => void submitReportRequest()}
          >
            {reportBusy ? 'Requesting…' : 'Request report'}
          </button>
        </div>
      )}

      {documents.length > 0 && (
        // Said plainly, where the act happens. R4's requirement is that retrieval is ATTRIBUTABLE, and
        // an audit record people are unaware of is a trap rather than a control.
        <p className="card__note">
          Open a document to retrieve it — the record keeps who retrieved it, and when.
        </p>
      )}

      {!canIngest ? (
        <p className="card__note">
          Adding documents to a record is restricted — your role on this project is read-only.
        </p>
      ) : (
        /*
          🔴 FOLDED AWAY ON A RECORD THAT ALREADY HOLDS FILES, and this is the tallest thing on the
          tab: a label, a hint, a drop zone, and a gate form per queued file. Most visits to this
          panel are to FIND a document, not to add one — so the reference-sized act sits behind its
          own control and the list gets the screen. The verb is never hidden, only folded.
        */
        <details
          className="docs__add"
          ref={addRef}
          open={addOpen}
          onToggle={(e) => setAddOpen(e.currentTarget.open)}
        >
          <summary>Add documents</summary>
          {/* Kept, and kept a real `<label htmlFor>`: it is the hidden file input's only accessible
              name. The summary above is the disclosure control, not a label for the field. */}
          <label className="df__label" htmlFor="proj-docs">
            Choose or drop files
          </label>
          <p className="df__hint">
            Each file is recorded on the project with who added it and when. Adding a document
            re-underwrites the deal against the record’s full document set.
          </p>

          {/*
            R10's drop zone. The file input is kept and hidden rather than replaced: it is the
            keyboard and screen-reader path, and a div with a drag handler is reachable by neither.
            The zone carries `role="button"` and an Enter/Space handler so it opens the same picker.

            ⚠️ `e.target.value = ''` after a pick, or choosing the SAME file twice in a row fires no
            change event and looks like the drop silently failed. `DocUpload` carries the same line.
          */}
          <div
            className={`dz docs__dz${dragging ? ' dz--over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (!busy) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e: DragEvent<HTMLDivElement>) => {
              e.preventDefault();
              setDragging(false);
              if (!busy) addFiles(e.dataTransfer.files);
            }}
            onClick={() => !busy && fileInput.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !busy) fileInput.current?.click();
            }}
          >
            <input
              ref={fileInput}
              id="proj-docs"
              type="file"
              multiple
              className="dz__input"
              disabled={busy}
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <div className="dz__icon" aria-hidden="true">
              ⬆
            </div>
            <p className="dz__lead">
              <strong>Drop documents here</strong> or click to browse
            </p>
            <p className="dz__hint">
              Drop as many as you like. We’ll read each filename for a likely type — confirm it, say
              whether it’s new or an update, and add them together.
            </p>
          </div>

          {pending.map((p, i) => (
            <div className="docs__gate" key={`${p.file.name}-${i}`}>
              <span className="docs__gatename">
                {p.file.name}
                {/* Once you can drag files in by the handful, taking one out has to be possible
                    without clearing the queue and starting again. */}
                <button
                  type="button"
                  className="docs__drop"
                  aria-label={`Remove ${p.file.name}`}
                  disabled={busy}
                  onClick={() => removeFile(i)}
                >
                  ✕
                </button>
              </span>
              <label className="docs__q">
                <span>
                  Document type
                  {/*
                    THE HONEST CHIP. It says the guess is a guess and where it came from, and it
                    disappears the moment the person answers — a "suggested" label still showing next
                    to a confirmed answer would misdescribe what the record holds.
                  */}
                  {!p.confirmed && p.suggested !== null && (
                    <span className="docs__chip">suggested from the filename — confirm it</span>
                  )}
                  {p.confirmed && <span className="docs__chip docs__chip--ok">confirmed</span>}
                </span>
                <select
                  value={p.documentType}
                  disabled={busy}
                  // Choosing from the list IS the confirmation — picking a type is an answer whether
                  // or not it happens to equal the guess.
                  onChange={(e) =>
                    update(i, { documentType: e.target.value, confirmed: e.target.value !== '' })
                  }
                >
                  <option value="">Choose a type…</option>
                  {sortedForDisplay().map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                {/*
                  🔴 REQUIRED, and its absence would have made the guess unacceptable — literally.
                  The select is PRE-FILLED with the suggestion, and re-picking the value a select
                  already holds fires no `change` event. Without this button the only way to confirm a
                  correct guess would be to pick something else and pick it back, so the common case —
                  the guess is right — would be the most awkward one. Found by walking the interaction,
                  not by a test: the markup was correct and every assertion passed.
                */}
                {!p.confirmed && p.documentType !== '' && (
                  <button
                    type="button"
                    className="docs__confirm"
                    disabled={busy}
                    onClick={() => update(i, { confirmed: true })}
                  >
                    Confirm {p.documentType}
                  </button>
                )}
              </label>
              <label className="docs__q">
                <span>New or an update?</span>
                <select
                  value={p.lineage}
                  disabled={busy}
                  onChange={(e) =>
                    update(i, {
                      lineage: e.target.value as 'new' | 'update',
                      // Clear a stale target when switching back to `new`, so a hidden value cannot
                      // ride along and supersede a document the reader is no longer looking at.
                      ...(e.target.value === 'new' ? { targetDocumentId: '' } : {}),
                    })
                  }
                >
                  <option value="new">New document</option>
                  <option value="update" disabled={documents.length === 0}>
                    Update to an existing document
                  </option>
                </select>
              </label>
              {p.lineage === 'update' && (
                <label className="docs__q">
                  <span>Which document does it replace?</span>
                  <select
                    value={p.targetDocumentId}
                    disabled={busy}
                    onChange={(e) => update(i, { targetDocumentId: e.target.value })}
                  >
                    <option value="">Choose one…</option>
                    {documents.map((d) => (
                      <option key={d.documentId} value={d.documentId}>
                        {updateTargetLabel(d)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          ))}

          {error && <p className="notice error docs__error">{error}</p>}

          {pending.length > 0 && (
            <button
              type="button"
              className="docs__submit"
              disabled={busy || incomplete}
              onClick={() => void submit()}
            >
              {busy
                ? 'Adding…'
                : `Add ${pending.length} document${pending.length === 1 ? '' : 's'} and re-underwrite`}
            </button>
          )}
          {/* Say WHICH question is unanswered. "Something is incomplete" on a queue of nine files is
              a puzzle, not a message. */}
          {unconfirmed > 0 && (
            <p className="card__note">
              {unconfirmed === 1
                ? 'One document still needs its type confirmed.'
                : `${unconfirmed} documents still need their type confirmed.`}
            </p>
          )}
          {missingTarget && (
            <p className="card__note">
              An update has to name the document it replaces before it can be added.
            </p>
          )}
        </details>
      )}
    </section>
  );
}
